import React from 'react';
import { ExclamationCircleFilled, GlobalOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import { ChainOfCustodyItem } from '@/apis/types';
import {
  AICallIcon,
  AtRickCOCIcon,
  COCGxPReportIcon,
  LogiblocksIcon,
  MailWhiteIcon,
  OneTimeTiemlineIcon,
  PhoneIcon,
} from '@/assets/icons/icons';
import air20 from '@/assets/images/20air.png';
import { FileSvgIcon } from '@/assets/images';
import { CoCEventType } from '@/common/enum';
import { getInitials } from '@/common/helper';
import styles from './CoCTimelineDot.module.css';

interface CoCTimelineDotProps {
  custody: ChainOfCustodyItem;
  /** Half the measured height of the event row; undefined until measured. */
  halfHeight?: number;
  /** Avatar colour of the comment author. */
  userColor?: string;
}

type DotKind =
  | 'comment'
  | 'risk'
  | 'customerNotification'
  | 'oneTime'
  | 'critical'
  | 'file'
  | 'aiCall'
  | 'gxp'
  | 'workflow'
  | 'awb'
  | 'customs'
  | 'plain';

const getDotKind = (custody: ChainOfCustodyItem): DotKind => {
  if (custody.type === CoCEventType.Comment) return 'comment';
  if (custody.type === CoCEventType.Alert && custody.is_critical === 0)
    return 'risk';
  if (custody.type === CoCEventType.CustomerNotification)
    return 'customerNotification';
  if (custody.type === CoCEventType.OneTimeNotification) return 'oneTime';
  if (custody.type === CoCEventType.Alert && custody.is_critical === 1)
    return 'critical';
  if (custody.file && custody.type === CoCEventType.FileUpload) return 'file';
  if (custody.type === CoCEventType.AiCallAlert) return 'aiCall';
  if (custody.type === CoCEventType.GxPReport) return 'gxp';
  if (custody.type === CoCEventType.WorkflowAction) return 'workflow';
  if (custody.handler === 'AWB') return 'awb';
  if (custody.handler === 'customs') return 'customs';
  return 'plain';
};

const WRAPPER_CLASS: Partial<Record<DotKind, string>> = {
  file: styles.file,
  awb: styles.awb,
  customs: styles.customs,
  plain: styles.plain,
};

const AutomationCircle: React.FC<{ testId?: string }> = ({ testId }) => (
  <div data-testid={testId} className={styles.circle}>
    <LogiblocksIcon size={16} color='white' />
  </div>
);

const DotGlyph: React.FC<CoCTimelineDotProps> = ({ custody, userColor }) => {
  switch (getDotKind(custody)) {
    case 'comment':
      return custody.is_from_automation ? (
        <AutomationCircle testId='automation-comment-icon' />
      ) : (
        <Avatar
          size={36}
          className='comment-avatar'
          style={{ backgroundColor: userColor || 'var(--gray-600)' }}
        >
          {getInitials(String(custody.created_by))}
        </Avatar>
      );
    case 'risk':
      return <AtRickCOCIcon />;
    case 'customerNotification':
      return (
        <div data-testid='customer-notification-icon' className={styles.circle}>
          {custody.notification_method === 'EMAIL' ? (
            <MailWhiteIcon />
          ) : (
            <PhoneIcon width={16} height={16} color='white' />
          )}
        </div>
      );
    case 'oneTime':
      return custody.is_from_automation ? (
        <AutomationCircle testId='automation-notification-icon' />
      ) : (
        <OneTimeTiemlineIcon />
      );
    case 'critical':
      return (
        <div className={`${styles.circle} ${styles.danger}`}>
          <span className={styles.glyph}>
            <ExclamationCircleFilled />
          </span>
        </div>
      );
    case 'file':
      return (
        <img
          width={16}
          src={FileSvgIcon}
          alt=''
          className='zl-invert-on-dark'
        />
      );
    case 'aiCall':
      return (
        <div className={styles.circle}>
          <span className={`h-4 ${styles.glyph}`}>
            <AICallIcon color='white' />
          </span>
        </div>
      );
    case 'gxp':
      return (
        <div className={styles.gxp}>
          <span className='h-4'>
            <COCGxPReportIcon />
          </span>
        </div>
      );
    case 'workflow':
      return <AutomationCircle />;
    case 'awb':
      return (
        <img
          src={air20}
          className={`${styles.awbIcon} zl-invert-on-dark`}
          alt='flight'
        />
      );
    case 'customs':
      return <GlobalOutlined className={styles.globe} />;
    case 'plain':
      return <div className={styles.defaultDot} />;
  }
};

/**
 * Dot or icon drawn on the Chain of Custody timeline rail for one event. Shared
 * by the internal drawer and the external shipment view so both pick the same
 * icon for an event type and follow dark mode the same way.
 *
 * @param custody - The Chain of Custody event the dot represents.
 * @param halfHeight - Half the measured height of the event row, used to centre the dot on it.
 * @param userColor - Avatar colour for comment authors.
 */
const CoCTimelineDot: React.FC<CoCTimelineDotProps> = ({
  custody,
  halfHeight,
  userColor,
}) => {
  const kind = getDotKind(custody);
  const offset = kind === 'file' ? 12 : 16;
  const wrapperProps =
    kind === 'workflow' ? { 'data-testid': 'workflow-action-icon' } : {};

  return (
    <span
      {...wrapperProps}
      className={`${styles.dot} ${WRAPPER_CLASS[kind] ?? ''}`}
      // eslint-disable-next-line react/forbid-dom-props -- position comes from the event row height measured at runtime (halfHeight)
      style={{ top: halfHeight === undefined ? 0 : halfHeight + offset }}
    >
      <DotGlyph custody={custody} userColor={userColor} />
    </span>
  );
};

export default CoCTimelineDot;
